import React, { useEffect, useMemo, useState } from "react";

import {
  TextField,
  Typography,
  Box,
  Alert,
  IconButton,
  Collapse
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  useForm,
  Controller
} from "react-hook-form";

import { useEmployees } from "../../context/EmployeesContext";
import AppSelect from "../../ui/AppSelect";
import AppDatePicker from "../../ui/AppDatePicker";
import RichTextEditor from "../../ui/RichTextEditor";
import CapacitySlider from "../../ui/CapacitySlider";

import {
  getProjects
} from "../../services/projectService";

import ResourceCalendar from "./ResourceCalendar";

import CapacityCalendar from "./CapacityCalendar";

// Match the query against the full name or any individual word's start (e.g.
// "Shah" matches "Bhumit Shah"), normalizing whitespace/case for reliability
// against synced data that may contain stray double-spaces.
const normalizeName = (value) => (value || "").toLowerCase().replace(/\s+/g, " ").trim();

// react-select's filterOption signature: (option, rawInput) => boolean.
const employeeSelectFilterOption = (option, rawInput) => {
  const normalizedQuery = normalizeName(rawInput);

  if (!normalizedQuery) {
    return true;
  }

  const name = normalizeName(option.label);
  return name.startsWith(normalizedQuery) || name.split(" ").some((word) => word.startsWith(normalizedQuery));
};

const PROJECT_TYPE_OPTIONS = [
  { value: "Internal", label: "Internal" },
  { value: "Client", label: "Client" }
];

function ProjectForm({
  project,
  onSubmit
}) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors }
  } = useForm();

  const { employees } = useEmployees();
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employeeCapacities, setEmployeeCapacities] = useState({});
  const [projectManagerId, setProjectManagerId] = useState(project?.project_manager_id || "");
  const [fullyBookedDates, setFullyBookedDates] = useState([]);
  const [dateConflicts, setDateConflicts] = useState([]);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(true);

  const startDate = watch("start_date", project?.start_date || "");
  const endDate = watch("end_date", project?.end_date || "");
  const projectType = watch("project_type", project?.project_type || "Internal");

  const sortedEmployees = useMemo(
    () => [...employees].sort((a, b) => a.employee_name.localeCompare(b.employee_name)),
    [employees]
  );

  useEffect(() => {
    if (project) {
      const ids = Array.isArray(project.assigned_to)
        ? project.assigned_to.map((a) => (typeof a === "object" ? a.employee_id : a))
        : (project.assigned_to ? [project.assigned_to] : []);
      const caps = {};
      if (Array.isArray(project.assigned_to)) {
        project.assigned_to.forEach((a) => {
          if (typeof a === "object") caps[a.employee_id] = a.capacity_percent || 0;
        });
      }
      reset({
        project_name: project.project_name,
        description: project.description || "",
        assigned_to: ids,
        start_date: project.start_date || "",
        end_date: project.end_date || "",
        project_type: project.project_type || "Internal",
        client_name: project.client_name || "",
        project_manager_id: project.project_manager_id || "",
      });
      setSelectedEmployeeIds(ids);
      setEmployeeCapacities(caps);
      setProjectManagerId(project.project_manager_id || "");
    }
    else {
      reset({
        project_name: "",
        description: "",
        assigned_to: [],
        start_date: "",
        end_date: "",
        project_type: "Internal",
        client_name: "",
        project_manager_id: "",
      });
      setEmployeeCapacities({});
      setProjectManagerId("");
    }
  }, [project, reset]);

  const handleEmployeeChange = async (ids) => {
    setSelectedEmployeeIds(ids);
    setValue("assigned_to", ids, { shouldValidate: true });
    setDateConflicts([]);

    // Track dates where an employee is already fully committed (300%) across
    // other projects, purely as an informational warning (not a hard block) -
    // employees can legitimately be double/triple booked.
    const allBooked = [];
    for (const employeeId of ids) {
      try {
        const projects = await getProjects("", employeeId);
        projects.forEach((proj) => {
          if (proj.capacity_percent >= 300 && proj.start_date && proj.end_date && proj.project_id !== project?.project_id) {
            const start = new Date(proj.start_date);
            const end = new Date(proj.end_date);
            let current = new Date(start);
            while (current <= end) {
              const dateStr = current.toISOString().split("T")[0];
              if (!allBooked.includes(dateStr)) allBooked.push(dateStr);
              current.setDate(current.getDate() + 1);
            }
          }
        });
      } catch (error) {
        console.error("Error loading capacity dates:", error);
      }
    }
    setFullyBookedDates(allBooked);
  };

  useEffect(() => {
    if (startDate && endDate && fullyBookedDates.length > 0) {
      const conflicts = [];
      const start = new Date(startDate);
      const end = new Date(endDate);
      let current = new Date(start);

      while (current <= end) {
        const dateStr = current.toISOString().split("T")[0];
        if (fullyBookedDates.includes(dateStr)) {
          conflicts.push(dateStr);
        }
        current.setDate(current.getDate() + 1);
      }

      setDateConflicts(conflicts);
    } else {
      setDateConflicts([]);
    }
  }, [startDate, endDate, fullyBookedDates]);

  return (
    <form
      id="project-form"
      style={{ height: "100%" }}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit((data) => {
          const payload = {
            ...data,
            assigned_to: selectedEmployeeIds.map((id) => ({
              employee_id: id,
              capacity_percent: employeeCapacities[id] || 0,
            })),
            start_date: data.start_date || null,
            end_date: data.end_date || null,
            project_manager_id: projectManagerId ? Number(projectManagerId) : null,
            client_name: data.project_type === "Client" ? data.client_name : null,
          };
          onSubmit(payload);
        })(e);
      }}
    >
      {/* hidden field so react-hook-form tracks and validates assigned_to */}
      <input type="hidden" {...register("assigned_to", { validate: v => (Array.isArray(v) && v.length > 0) || "required" })} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 4fr) minmax(0, 1fr)" },
            gap: 3
          }}
        >
          {/* Left column: Project Name + Description */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Project Name <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Enter project name"
                error={Boolean(errors.project_name)}
                helperText={errors.project_name ? "Project name is required" : ""}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "background.paper"
                  },
                  "& .MuiFormHelperText-root": {
                    minHeight: 20,
                    mt: 0.7
                  }
                }}
                {...register("project_name", { required: true })}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Description
              </Typography>

              <Controller
                name="description"
                control={control}
                defaultValue={project?.description || ""}
                render={({ field }) => (
                  <RichTextEditor
                    className="project-description-editor"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Enter project description"
                    minHeight={310}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Right column: Details (Type, Client Name, Project Manager, Assign To, Dates) */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
              <Box
                onClick={() => setDetailsOpen((prev) => !prev)}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.4, cursor: "pointer" }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Details
                </Typography>
                <IconButton size="small">
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                  />
                </IconButton>
              </Box>

              <Collapse in={detailsOpen}>
                <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Project Type <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                    </Typography>

                    <AppSelect
                      options={PROJECT_TYPE_OPTIONS}
                      value={projectType}
                      onChange={(value) => setValue("project_type", value, { shouldValidate: true })}
                    />
                  </Box>

                  {projectType === "Client" && (
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                        Client Name
                      </Typography>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter client name"
                        error={Boolean(errors.client_name)}
                        helperText={errors.client_name ? "Client name is required" : ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper"
                          },
                          "& .MuiFormHelperText-root": {
                            minHeight: 20,
                            mt: 0.7
                          }
                        }}
                        {...register("client_name", { required: projectType === "Client" })}
                      />
                    </Box>
                  )}

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                     Report To <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                    </Typography>

                    <AppSelect
                      options={sortedEmployees.map((emp) => ({ value: emp.employee_id, label: emp.employee_name, avatar: emp.profile_picture }))}
                      value={projectManagerId}
                      onChange={(value) => setProjectManagerId(value)}
                      filterOption={employeeSelectFilterOption}
                      isClearable
                      withAvatar
                      placeholder="Search and select project manager"
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Assign To <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                    </Typography>

                    <AppSelect
                      isMulti
                      options={sortedEmployees.map((emp) => ({ value: emp.employee_id, label: emp.employee_name, avatar: emp.profile_picture }))}
                      value={selectedEmployeeIds}
                      onChange={handleEmployeeChange}
                      filterOption={employeeSelectFilterOption}
                      withAvatar
                      placeholder="Search and select employees"
                      error={Boolean(errors.assigned_to)}
                      helperText={errors.assigned_to ? "Please assign at least one employee" : ""}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Project Start Date
                    </Typography>

                    <AppDatePicker
                      selected={startDate ? new Date(startDate) : null}
                      onChange={(date) => {
                        setValue("start_date", date ? date.toISOString().split("T")[0] : "", { shouldValidate: true });
                        setStartDateError("");
                      }}
                      placeholder="Select start date"
                      popperPlacement="bottom-start"
                      error={Boolean(startDateError)}
                      helperText={startDateError}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                      Project End Date
                    </Typography>

                    <AppDatePicker
                      selected={endDate ? new Date(endDate) : null}
                      onChange={(date) => {
                        setValue("end_date", date ? date.toISOString().split("T")[0] : "", { shouldValidate: true });
                        setEndDateError("");
                      }}
                      minDate={startDate ? new Date(startDate) : null}
                      placeholder="Select end date"
                      popperPlacement="bottom-end"
                      error={Boolean(endDateError)}
                      helperText={endDateError}
                    />
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>

        {/* Full-width sections below: capacity calendars + resource allocation */}
        {selectedEmployeeIds.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                color: "#475569",
                fontWeight: 600,
                mb: 1.5
              }}
            >
              Current Capacity & Calendar
            </Typography>
            {selectedEmployeeIds.map((empId) => {
              const emp = employees.find((e) => e.employee_id === empId);
              return (
                <Box
                  key={empId}
                  mb={3}
                  sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.default" }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#334155", mb: 1.5 }}>
                    {emp ? emp.employee_name : `Employee ${empId}`}
                  </Typography>

                  <Box mb={2}>
                    <ResourceCalendar employeeId={empId} />
                  </Box>

                  <CapacityCalendar employeeId={empId} />
                </Box>
              );
            })}
          </Box>
        )}

        {dateConflicts.length > 0 && (
          <Alert severity="warning">
            ⚠️ Employee is already fully committed (300%) on {dateConflicts.length} date(s) in this range:
            <br />
            {dateConflicts.slice(0, 5).join(", ")}
            {dateConflicts.length > 5 && ` and ${dateConflicts.length - 5} more`}
          </Alert>
        )}

        {selectedEmployeeIds.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 1.5 }}>
              Capacity Allocation
            </Typography>
            {selectedEmployeeIds.map((empId) => {
              const emp = employees.find((e) => e.employee_id === empId);
              return (
                <CapacitySlider
                  key={empId}
                  name={emp ? emp.employee_name : `Employee ${empId}`}
                  avatarUrl={emp?.profile_picture}
                  value={employeeCapacities[empId] || 0}
                  onChange={(e, val) => setEmployeeCapacities((prev) => ({ ...prev, [empId]: val }))}
                />
              );
            })}
          </Box>
        )}
      </Box>
    </form>
  );
}

export default ProjectForm;
